/**
 * useMouseLogger
 *
 * Amacı:
 * - Fare ve kaydırma etkileşimlerini (mousemove, click, mousedown, mouseup, scroll) dinleyip
 *   HER olayı ANLIK olarak (buffer kullanmadan) sunucuya göndermek.
 * - Gürültüyü azaltmak için mousemove olaylarında basit throttle (>=100ms) ve delta filtresi (dx>=5 veya dy>=5) uygular.
 *
 * Parametreler:
 * - assetId: string | number — Log kayıtlarını ilişkilendirmek için kullanılan kimlik (null/undefined ise loglanmaz).
 *
 * Loglanan Olaylar (eventType) ve Metadata:
 * - 'mousemove' — { x, y, target }
 * - 'click'     — { x, y, target }
 * - 'mousedown' — { x, y, target }
 * - 'mouseup'   — { x, y, target }
 * - 'scroll'    — { x, y } // x=window.scrollX, y=window.scrollY
 *   Not: target, e.target?.tagName bilgisinden türetilir.
 *
 * Davranış ve Kenar Durumları:
 * - Olaylar doğrudan `service.create` ile gönderilir; buffer yoktur.
 * - Çok yüksek frekanslı mousemove spam'ini azaltmak için:
 *      * Ardışık hareketler arasında <100ms ise atlanır.
 *      * Hem dx<5 hem dy<5 ise atlanır.
 * - Bileşen unmount olduğunda event listener'lar temizlenir.
 * - SSR ortamında kullanılacaksa window/document varlığı kontrol edilmiş olmalıdır (bu hook tarayıcıda çağrılmalı).
 *
 * Kullanım Örneği:
 *
 * function PlayerPage({ assetId }) {
 *   useMouseLogger(assetId);
 *   return <Player />;
 * }
 */

import { useEffect, useRef, useCallback } from 'react';
import LogService from "../../service/log/LogService";

const service = new LogService('logMouse');

export const useMouseLogger = (assetId) => {
    const lastMoveRef = useRef({ x: 0, y: 0, time: 0 });

    const log = useCallback((eventType, metadata) => {
        if (!assetId) return;
        try {
            service.create({
                eventType,
                assetId,
                metadata: JSON.stringify(metadata)
            });
        } catch (e) {
            // Sessizce yut (isteğe göre konsola yazılabilir)
        }
    }, [assetId]);

    useEffect(() => {
        if (!assetId) return; // geçersiz id'de hiç dinleyici ekleme

        const cleanupFns = [];

        const events = [
            { type: 'mousemove', includeMousePos: true },
            { type: 'click', includeMousePos: true },
            { type: 'mousedown', includeMousePos: true },
            { type: 'mouseup', includeMousePos: true },
            { type: 'scroll', includeMousePos: false },
        ];

        events.forEach(({ type, includeMousePos }) => {
            const handler = (e) => {
                const now = Date.now();
                const metadata = {};

                if (includeMousePos) {
                    const dx = Math.abs(e.clientX - lastMoveRef.current.x);
                    const dy = Math.abs(e.clientY - lastMoveRef.current.y);
                    const dt = now - lastMoveRef.current.time;

                    if (type === 'mousemove') {
                        if (dt < 100 || (dx < 5 && dy < 5)) return; // throttle + delta filtresi
                        lastMoveRef.current = { x: e.clientX, y: e.clientY, time: now };
                    }
                    metadata.x = e.clientX;
                    metadata.y = e.clientY;
                } else {
                    metadata.x = window.scrollX;
                    metadata.y = window.scrollY;
                }

                metadata.target = e.target?.tagName;

                log(type, metadata);
            };

            window.addEventListener(type, handler);
            cleanupFns.push(() => window.removeEventListener(type, handler));
        });

        return () => {
            cleanupFns.forEach((fn) => fn());
        };
    }, [assetId, log]);
};

